import * as Sentry from "@sentry/react";

import {
  ConversationStatus,
  conversationStatusMeta,
} from "src/views/assistant/components/conversationStatus";

/**
 * Single owner of the browser-tab favicon.
 *
 * Two things want to paint on it: the live conversation state on the
 * AtomAssist page (working / complete / error) and the unread notification
 * dot everywhere else. Both write through this module so neither overwrites
 * the other, feeding one state and one renderer with a fixed precedence: a
 * conversation status disc wins while a conversation is working, done, or in
 * error, otherwise the unread dot shows, otherwise the page's own icon.
 * "ready" reports no news, so it paints nothing and yields to the dot.
 */

const FAVICON_ID = "page-favicon";
const FALLBACK_FAVICON = "/favicon.png";
const SIZE = 48;

// Every badge sits in the same bottom-right disc so the tab reads as one
// system. Favicons are commonly rendered at only 16px, so the disc spans a bit
// over half the icon's width (about 9px of a 16px render) to read as a state
// rather than a stray pixel.
const DISC_X = 34;
const DISC_Y = 34;
const DISC_OUTER_RADIUS = 14;
const DISC_RING_RADIUS = 12;
const DISC_FILL_RADIUS = 10.25;
const UNREAD_COLOR = "#d32f2f";

type Layer = { status: ConversationStatus | null; unread: boolean };

let status: ConversationStatus | null = null;
let statusOwner: string | null = null;
let unreadCount = 0;
// The page's own icon (index.html swaps in a product logo on some
// deployments), captured before the first badge replaces it.
let baseHref: string | undefined;
let imagePromise: Promise<HTMLImageElement> | undefined;
// Latest requested render; an older async draw must not overwrite it.
let generation = 0;
const rendered = new Map<string, string>();

const getBaseHref = () => baseHref ?? FALLBACK_FAVICON;

const getLink = (): HTMLLinkElement => {
  const existing = document.getElementById(FAVICON_ID);
  if (existing instanceof HTMLLinkElement) {
    const href = existing.getAttribute("href");
    // Capture once, and never a badge drawn here.
    if (href && !href.startsWith("data:")) baseHref ??= href;
    return existing;
  }
  const link = document.createElement("link");
  link.id = FAVICON_ID;
  link.rel = "icon";
  link.href = getBaseHref();
  document.head.appendChild(link);
  return link;
};

const loadBaseImage = () => {
  if (!imagePromise) {
    imagePromise = new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = "anonymous";
      image.onload = () => resolve(image);
      image.onerror = (error) => {
        // Let the next render try again instead of caching the failure.
        imagePromise = undefined;
        reject(error);
      };
      image.src = getBaseHref();
    });
  }
  return imagePromise;
};

const drawStatusGlyph = (
  context: CanvasRenderingContext2D,
  state: ConversationStatus,
) => {
  context.strokeStyle = "#ffffff";
  context.fillStyle = "#ffffff";
  context.lineCap = "round";
  context.lineJoin = "round";
  context.lineWidth = 3.5;

  if (state === "working") {
    context.beginPath();
    context.arc(DISC_X, DISC_Y, 5, -Math.PI / 2, Math.PI * 0.85);
    context.stroke();
  }
  if (state === "complete") {
    context.beginPath();
    context.moveTo(DISC_X - 5.2, DISC_Y);
    context.lineTo(DISC_X - 1.2, DISC_Y + 4);
    context.lineTo(DISC_X + 5.8, DISC_Y - 4.7);
    context.stroke();
  }
  if (state === "error") {
    context.fillRect(DISC_X - 1.5, DISC_Y - 6, 3, 7.5);
    context.beginPath();
    context.arc(DISC_X, DISC_Y + 6, 1.75, 0, Math.PI * 2);
    context.fill();
  }
};

const drawDisc = (context: CanvasRenderingContext2D, color: string) => {
  // A double ring keeps the disc legible on both light and dark tab strips.
  context.fillStyle = "rgba(255, 255, 255, 0.98)";
  context.beginPath();
  context.arc(DISC_X, DISC_Y, DISC_OUTER_RADIUS, 0, Math.PI * 2);
  context.fill();

  context.fillStyle = "#102d35";
  context.beginPath();
  context.arc(DISC_X, DISC_Y, DISC_RING_RADIUS, 0, Math.PI * 2);
  context.fill();

  context.fillStyle = color;
  context.beginPath();
  context.arc(DISC_X, DISC_Y, DISC_FILL_RADIUS, 0, Math.PI * 2);
  context.fill();
};

const layerKey = (layer: Layer) =>
  `${layer.status ?? ""}|${layer.unread ? "unread" : ""}`;

const compose = async (layer: Layer): Promise<string> => {
  const key = layerKey(layer);
  const cached = rendered.get(key);
  if (cached) return cached;

  const image = await loadBaseImage();
  const canvas = document.createElement("canvas");
  canvas.width = SIZE;
  canvas.height = SIZE;
  const context = canvas.getContext("2d");
  if (!context) return getBaseHref();

  context.clearRect(0, 0, SIZE, SIZE);
  context.drawImage(image, 0, 0, SIZE, SIZE);
  if (layer.status) {
    drawDisc(context, conversationStatusMeta[layer.status].color);
    drawStatusGlyph(context, layer.status);
  } else if (layer.unread) {
    drawDisc(context, UNREAD_COLOR);
  }

  const href = canvas.toDataURL("image/png");
  rendered.set(key, href);
  return href;
};

// "ready" means "nothing to report", so it must not outrank the unread dot:
// every conversation page reports at least "ready", and a grey no-glyph disc
// would hide the red dot on the page users spend the most time on. The header
// chip still shows "Ready" — this only drops it from the painted layer.
const paintedStatus = (): ConversationStatus | null =>
  status === "ready" ? null : status;

const currentLayer = (): Layer => {
  const painted = paintedStatus();
  return { status: painted, unread: painted === null && unreadCount > 0 };
};

const render = () => {
  const link = getLink();
  const layer = currentLayer();
  // Reflects the tab's acknowledged status, including a "ready" that paints
  // nothing, so an E2E test can read the state the tab is showing — the same
  // one the header chip shows, since both come from useConversationTabStatus.
  const markStatus = () => {
    if (status) link.dataset.conversationStatus = status;
    else delete link.dataset.conversationStatus;
  };

  const current = ++generation;
  if (!layer.status && !layer.unread) {
    markStatus();
    link.href = getBaseHref();
    return;
  }
  void compose(layer)
    .then((href) => {
      // Only claim the status once the icon that shows it is actually live.
      if (current !== generation) return;
      markStatus();
      link.href = href;
    })
    .catch((error) => {
      // Keep the page's own icon; the title prefix still carries the unread
      // count. An `<img>` load rejects with a DOM Event, which is the routine
      // failure (a missing or cross-origin icon) and stays console-only.
      // Everything `instanceof Error` is reported — a TypeError in
      // drawDisc/drawStatusGlyph, a status missing from
      // conversationStatusMeta, or the SecurityError `toDataURL` throws on a
      // tainted canvas — because those silently disable all tab badging.
      console.warn(
        `tabFavicon: could not compose the tab icon from ${getBaseHref()}`,
        error,
      );
      if (error instanceof Error) {
        Sentry.captureException(error, {
          extra: { baseHref: getBaseHref(), layer },
        });
      }
      if (current !== generation) return;
      markStatus();
      link.href = getBaseHref();
    });
};

/**
 * Show (or, with null, clear) the conversation state. `owner` identifies the
 * caller so an unmounting conversation cannot clear a state a newer one set.
 */
export const setConversationStatus = (
  owner: string,
  next: ConversationStatus | null,
) => {
  if (next === null) {
    if (statusOwner !== owner) return;
    statusOwner = null;
    status = null;
  } else {
    statusOwner = owner;
    status = next;
  }
  render();
};

export const setUnreadCount = (count: number) => {
  if (count === unreadCount) return;
  unreadCount = count;
  render();
};

/** Test hook: forget every cached icon and captured base. */
export const resetTabFavicon = () => {
  status = null;
  statusOwner = null;
  unreadCount = 0;
  baseHref = undefined;
  imagePromise = undefined;
  generation = 0;
  rendered.clear();
};
